import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageSquare, Bug, Lightbulb, HelpCircle, CircleDashed, CheckCircle2, Clock, ArrowLeft, Plus } from "lucide-react";

const TYPE_META = {
  bug:        { label: "Bug",        icon: Bug,          tone: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  suggestion: { label: "Suggestion", icon: Lightbulb,    tone: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  question:   { label: "Question",   icon: HelpCircle,   tone: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  other:      { label: "Other",      icon: MessageSquare, tone: "text-slate-300 bg-slate-500/10 border-slate-500/30" },
};

const STATUS_META = {
  open:         { label: "Open",         icon: CircleDashed,  tone: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  in_progress:  { label: "In progress",  icon: Clock,         tone: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  resolved:     { label: "Resolved",     icon: CheckCircle2,  tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

export default function PartnerFeedback() {
  const [threads, setThreads] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      const { data } = await api.get("/commissions/partner-feedback");
      setThreads(data.threads || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load feedback");
    }
  }
  useEffect(() => { reload(); }, []);

  const openThread = useMemo(
    () => threads?.find((t) => t.id === openId) || null,
    [threads, openId]
  );

  if (openThread) {
    return (
      <ThreadView
        thread={openThread}
        onBack={() => { setOpenId(null); reload(); }}
        role="partner"
        onReplied={reload}
        busy={busy}
        setBusy={setBusy}
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="partner-feedback-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl tracking-tight font-medium">Ideas &amp; issues</h1>
          <p className="text-slate-400 mt-1.5 text-sm max-w-2xl">
            Anything on your mind — a bug, a suggestion, or a quick question — drop it here and the Steele Cloud team will get back to you. All your conversations stay on this page.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="partner-feedback-new-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> New message
          </button>
        )}
      </header>

      {creating && (
        <NewFeedbackForm
          onCancel={() => setCreating(false)}
          onCreated={(doc) => { setCreating(false); reload(); setOpenId(doc.id); }}
        />
      )}

      {threads === null ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-10 text-center" data-testid="partner-feedback-empty">
          <MessageSquare className="w-8 h-8 mx-auto text-slate-600 mb-3" />
          <div className="text-slate-300 font-medium">No conversations yet</div>
          <div className="text-slate-500 text-sm mt-1">
            Spot a bug or have an idea? Hit “New message” to start a thread.
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="partner-feedback-list">
          {threads.map((t) => (
            <ThreadRow key={t.id} thread={t} onOpen={() => setOpenId(t.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ThreadRow({ thread, onOpen }) {
  const t = TYPE_META[thread.type] || TYPE_META.other;
  const s = STATUS_META[thread.status] || STATUS_META.open;
  const TIcon = t.icon; const SIcon = s.icon;
  const last = thread.messages?.[thread.messages.length - 1];
  const unread = !!thread.is_unread_partner;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`partner-feedback-row-${thread.id}`}
      className={`w-full text-left rounded-xl border p-5 transition-colors ${
        unread
          ? "border-cyan-500/40 bg-cyan-500/5 hover:bg-cyan-500/10"
          : "border-slate-800 bg-slate-900/60 hover:bg-slate-900"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${t.tone}`}>
          <TIcon className="w-3 h-3" /> {t.label}
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${s.tone}`}>
          <SIcon className="w-3 h-3" /> {s.label}
        </div>
        {unread && (
          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-500 text-slate-950 text-[10px] font-bold uppercase tracking-wider" data-testid={`partner-feedback-unread-${thread.id}`}>
            New reply
          </span>
        )}
      </div>
      <div className="mt-3 font-semibold text-slate-50 truncate">{thread.subject}</div>
      {last && (
        <div className="mt-1.5 text-sm text-slate-400 line-clamp-2 whitespace-pre-wrap">
          <span className="text-slate-500 mr-1">
            {last.author_role === "admin" ? "Steele Cloud:" : "You:"}
          </span>
          {last.body}
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-500 font-mono">
        {new Date(thread.last_activity_at).toLocaleString()}
        {thread.messages?.length > 1 && ` · ${thread.messages.length} messages`}
      </div>
    </button>
  );
}

export function NewFeedbackForm({ onCancel, onCreated, role = "partner" }) {
  const [type, setType] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) { toast.error("Give it a subject and a message"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/partner-feedback", {
        type, subject: subject.trim(), body: body.trim(),
      });
      toast.success(role === "admin"
        ? "Internal note saved — partners can't see this."
        : "Message sent — we'll take a look!");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-slate-800 bg-slate-900 p-6 md:p-8 space-y-5"
      data-testid={`${role}-feedback-new-form`}
    >
      {role === "admin" && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 flex items-center gap-2">
          <Lightbulb className="w-3.5 h-3.5" />
          Internal note — visible to admins only. Partners will never see this.
        </div>
      )}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Type</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(TYPE_META).map(([k, m]) => {
            const Icon = m.icon;
            const active = type === k;
            return (
              <button
                type="button"
                key={k}
                onClick={() => setType(k)}
                data-testid={`partner-feedback-type-${k}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wider transition-colors ${
                  active ? m.tone : "border-slate-700 bg-slate-950/40 text-slate-400 hover:text-slate-200"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Subject</label>
        <input
          data-testid="partner-feedback-subject"
          type="text"
          maxLength={200}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          autoFocus
          className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          placeholder="One-line summary — e.g. Ledger CSV export missing plan column"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Message</label>
        <textarea
          data-testid="partner-feedback-body"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 resize-y"
          placeholder="What's going on? Steps to reproduce, screenshots, or your idea — as much or as little as you like."
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="partner-feedback-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
        >
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="partner-feedback-cancel"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function ThreadView({ thread, onBack, role, onReplied, busy, setBusy }) {
  const [reply, setReply] = useState("");
  const t = TYPE_META[thread.type] || TYPE_META.other;
  const s = STATUS_META[thread.status] || STATUS_META.open;
  const SIcon = s.icon;

  async function send(e) {
    e.preventDefault();
    if (!reply.trim()) { toast.error("Message can't be empty"); return; }
    setBusy(true);
    try {
      await api.post(`/commissions/partner-feedback/${thread.id}/reply`, {
        body: reply.trim(),
      });
      setReply("");
      onReplied?.();
      toast.success("Reply sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reply failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid={`feedback-thread-${thread.id}`}>
      <div>
        <button
          type="button"
          onClick={onBack}
          data-testid="feedback-thread-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all messages
        </button>
      </div>

      <header className="rounded-xl border border-slate-800 bg-slate-900 p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${t.tone}`}>
            {t.label}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${s.tone}`} data-testid={`feedback-thread-status-${thread.status}`}>
            <SIcon className="w-3 h-3" /> {s.label}
          </span>
          {role === "admin" && thread.partner_code && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-300 text-[10px] font-mono tracking-wider">
              partner: {thread.partner_code}
            </span>
          )}
        </div>
        <h2 className="font-display text-2xl tracking-tight mt-3">{thread.subject}</h2>
        <div className="text-xs text-slate-500 mt-1 font-mono">
          Opened {new Date(thread.created_at).toLocaleString()} by {thread.created_by_name || thread.created_by_email}
        </div>
      </header>

      <div className="space-y-3" data-testid="feedback-thread-messages">
        {thread.messages?.map((m) => {
          const mine = (role === "partner" && m.author_role === "partner") ||
                       (role === "admin" && m.author_role === "admin");
          return (
            <div
              key={m.id}
              data-testid={`feedback-thread-msg-${m.id}`}
              className={`rounded-xl border p-4 ${
                mine ? "border-cyan-500/30 bg-cyan-500/5 ml-8" : "border-slate-800 bg-slate-900 mr-8"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-semibold ${mine ? "text-cyan-300" : "text-slate-300"}`}>
                  {m.author_role === "admin" ? "Steele Cloud" : (m.author_name || m.author_email)}
                </span>
                <span className="text-[11px] text-slate-500 font-mono">
                  {new Date(m.created_at).toLocaleString()}
                </span>
              </div>
              <div className="text-sm text-slate-100 whitespace-pre-wrap leading-relaxed">
                {m.body}
              </div>
            </div>
          );
        })}
      </div>

      {thread.status !== "resolved" && (
        <form onSubmit={send} className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-3" data-testid="feedback-thread-reply-form">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block">Reply</label>
          <textarea
            data-testid="feedback-thread-reply-body"
            rows={4}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 resize-y"
            placeholder="Add to the conversation…"
          />
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="feedback-thread-reply-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
            >
              <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send reply"}
            </button>
          </div>
        </form>
      )}

      {thread.status === "resolved" && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-center" data-testid="feedback-thread-resolved-note">
          <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-400 mb-2" />
          <div className="text-sm text-slate-300">
            This conversation is marked resolved. Need to add more? Start a new thread from the list.
          </div>
        </div>
      )}
    </div>
  );
}
